import React, { useState } from 'react';
import { Stack, Group, Button, Text, Alert, Divider, Loader } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconPlus, IconAlertCircle } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Goal, CreateGoalData, UpdateGoalData } from '../../types';
import {
  useUserGoals,
  useCreateGoal,
  useUpdateGoal,
  useAcceptGoal,
  useCompleteGoal,
  useDeleteGoal,
} from '../../hooks/useApi';
import { GoalCard } from './GoalCard';
import { GoalModal } from './GoalModal';

import { showToast } from '../../utils/toast';
import { useUserStore } from '../../store/userStore';

interface GoalsTabProps {
  userId: string;
}

export const GoalsTab: React.FC<GoalsTabProps> = ({ userId }) => {
  const { t } = useTranslation();
  const currentUser = useUserStore((s) => s.user);
  const [showModal, setShowModal] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);

  const { data: goals = [], isLoading, error } = useUserGoals(userId);
  const createGoalMutation = useCreateGoal();
  const updateGoalMutation = useUpdateGoal();
  const acceptGoalMutation = useAcceptGoal();
  const completeGoalMutation = useCompleteGoal();
  const deleteGoalMutation = useDeleteGoal();

  // Разделяем цели на активные и завершенные
  const activeGoals = goals.filter((goal) => goal.status !== 'completed');
  const completedGoals = goals.filter((goal) => goal.status === 'completed');

  const handleCreateGoal = () => {
    setEditingGoal(null);
    setShowModal(true);
  };

  const handleEditGoal = (goal: Goal) => {
    setEditingGoal(goal);
    setShowModal(true);
  };

  const handleSubmitGoal = async (data: CreateGoalData | UpdateGoalData) => {
    try {
      if (editingGoal) {
        await updateGoalMutation.mutateAsync({
          goalId: editingGoal._id,
          data: data as UpdateGoalData,
        });
        showToast({
          title: t('messages.updated', { item: t('goals.title') }),
          message: '',
        });
      } else {
        await createGoalMutation.mutateAsync(data as CreateGoalData);
        showToast({
          title: t('messages.created', { item: t('goals.title') }),
          message: '',
        });
      }
      setShowModal(false);
      setEditingGoal(null);
    } catch (error) {
      console.error('Error submitting goal:', error);
      showToast({
        title: t('messages.error'),
        message: t('messages.errorSaving', { item: t('goals.title') }),
        type: 'error',
      });
    }
  };

  const handleAcceptGoal = async (goalId: string) => {
    try {
      await acceptGoalMutation.mutateAsync(goalId);
      showToast({
        title: t('goals.accepted'),
        message: '',
      });
    } catch (error) {
      console.error('Error accepting goal:', error);
      showToast({
        title: t('messages.error'),
        message: t('messages.errorAccepting', { item: t('goals.title') }),
        type: 'error',
      });
    }
  };

  const handleCompleteGoal = async (goalId: string, comment: string) => {
    try {
      await completeGoalMutation.mutateAsync({
        goalId,
        data: { completionComment: comment },
      });
      showToast({
        title: t('goals.completed'),
        message: '',
      });
      return true;
    } catch (error) {
      console.error('Error completing goal:', error);
      showToast({
        title: t('messages.error'),
        message: t('messages.errorCompleting', { item: t('goals.title') }),
        type: 'error',
      });
      return false;
    }
  };

  const handleConfirmDeleteGoal = async (goalId: string) => {
    try {
      await deleteGoalMutation.mutateAsync(goalId);
      showToast({
        title: t('messages.deleted', { item: t('goals.title') }),
        message: '',
      });
    } catch (error) {
      console.error('Error deleting goal:', error);
      showToast({
        title: t('messages.error'),
        message: t('messages.errorDeleting', { item: t('goals.title') }),
        type: 'error',
      });
    }
  };

  const handleDeleteGoal = (goal: Goal) => {
    modals.openConfirmModal({
      title: t('messages.confirmDelete', { item: goal.title }),
      centered: true,
      children: <Text size="sm">{t('messages.confirmDelete', { item: goal.title })}</Text>,
      labels: { confirm: t('common.delete'), cancel: t('common.cancel') },
      confirmProps: { color: 'red' },
      onCancel: () => {},
      onConfirm: () => handleConfirmDeleteGoal(goal._id),
    });
  };

  if (error) {
    return (
      <Alert icon={<IconAlertCircle size={16} />} title={t('messages.error')} color="red">
        {t('messages.errorLoading', { item: t('goals.title') })}
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <Group justify="center" py="xl">
        <Loader size="sm" />
      </Group>
    );
  }

  return (
    <div style={{ position: 'relative' }}>
      <Stack gap="lg">
        <Group justify="space-between" align="center">
          <Text fw={500}>
            {t('goals.active')} ({activeGoals.length})
          </Text>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={handleCreateGoal}
            size="sm"
            variant="light"
          >
            {t('goals.create')}
          </Button>
        </Group>

        {/* Активные цели */}
        <div>
          {activeGoals.length > 0 ? (
            <Stack gap="md">
              {activeGoals.map((goal) => (
                <GoalCard
                  key={goal._id}
                  goal={goal}
                  currentUserId={currentUser?._id || ''}
                  onEdit={() => handleEditGoal(goal)}
                  onDelete={() => handleDeleteGoal(goal)}
                  onAccept={() => handleAcceptGoal(goal._id)}
                  onComplete={(comment) => handleCompleteGoal(goal._id, comment)}
                />
              ))}
            </Stack>
          ) : (
            <Text c="dimmed" ta="center" py="xl">
              {t('goals.noActiveGoals')}
            </Text>
          )}
        </div>

        {/* Завершенные цели */}
        {completedGoals.length > 0 && (
          <>
            <Divider />
            <div>
              <Text fw={500} mb="md" c="teal">
                {t('goals.completed')} ({completedGoals.length})
              </Text>
              <Stack gap="md">
                {completedGoals.map((goal) => (
                  <GoalCard key={goal._id} goal={goal} currentUserId={currentUser?._id || ''} />
                ))}
              </Stack>
            </div>
          </>
        )}
      </Stack>

      <GoalModal
        opened={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingGoal(null);
        }}
        onSubmit={handleSubmitGoal}
        goal={editingGoal}
        userId={userId}
        isLoading={createGoalMutation.isPending || updateGoalMutation.isPending}
      />
    </div>
  );
};
